import React, { useEffect, useState } from 'react';
import ClayLayout from '@clayui/layout';
import { useForm } from 'react-hook-form';
import {
    basicDetailsFields,
    contactDetailsFields,
    defaultValueForSelect,
    scopeKeyLeadManagement,
    seniorManagementDetailsFields
} from '../../utils/Config';
import FormComponent from '../../generic-components/formComponent/FormComponent';
import Button from '@clayui/button';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    setFormId,
    setInquiryVersion,
    setIsChamberMember,
    setIsFormFilled,
    updateFormValues
} from '../../slices/businessExpansionSlice';
import { updateCommonValues } from '../../slices/commonSlice';
import { primaryButtonStyle, scrollToSection } from '../../utils/commonUtil';
import { otpService } from '../../services/otpService';
import { toast } from 'react-toastify';
import Loader from '../../generic-components/loader/Loader';
import { expansionInquiryService } from '../../services/expansionInquiryService';
import { convertDocumentItemToFile } from '../../utils/FileToBase64';

interface IContactDetail {
    setStep: React.Dispatch<React.SetStateAction<number>>;
}

const ContactDetail: React.FC<IContactDetail> = ({ setStep }) => {
    const dispatch = useAppDispatch();
    const formValues = useAppSelector((state) => state.businessExpansion);
    const commonValues = useAppSelector((state) => state.common);
    const { isFormFilled, isChamberMember } = formValues;
    const { businessEmail } = commonValues;
    const [uploadLicense, setUploadLicense] = useState<File[]>(
        formValues.attachtradeLicenseDoc || []
    );
    const [uploadMembership, setUploadMembership] = useState<File[]>(
        formValues.attachMembershipDoc || []
    );
    const [uploadCompanyProfile, setUploadCompanyProfile] = useState<File[]>(
        formValues.attachCompanyProfile || []
    );
    const [previousCountryData, setPreviousCountryData] = useState([]);
    console.log('uploadMembership', uploadMembership, uploadLicense);
    const [loading, setLoading] = useState(false);
    const fileHandling = {
        attachtradeLicenseDoc: {
            selectedFiles: uploadLicense,
            setSelectedFiles: setUploadLicense
        },
        attachMembershipDoc: {
            selectedFiles: uploadMembership,
            setSelectedFiles: setUploadMembership
        },
        attachCompanyProfile: {
            selectedFiles: uploadCompanyProfile,
            setSelectedFiles: setUploadCompanyProfile
        }
    };
    const {
        control,
        register,
        formState: { errors: formErrors },
        watch,
        handleSubmit,
        setError,
        setValue,
        getValues
    } = useForm<any>({
        mode: 'all',
        defaultValues: {
            ...formValues,
            ...commonValues,
            haveYouFill:
                isFormFilled === false
                    ? 'no'
                    : isFormFilled === true
                      ? 'yes'
                      : '',
            member:
                isChamberMember === true
                    ? 'yes'
                    : isChamberMember === false
                      ? 'no'
                      : ''
        }
    });
    const haveYouFill = watch('haveYouFill');
    const member = watch('member');

    const managedFields = React.useMemo(
        () =>
            [
                ...basicDetailsFields,
                ...contactDetailsFields,
                ...seniorManagementDetailsFields
            ].map((f) => f.name),
        []
    );

    const getManagedValues = (currentValues: any) => {
        const payload: any = {};
        managedFields.forEach((name) => {
            if (currentValues[name] !== undefined) {
                payload[name] = currentValues[name];
            }
        });
        // Overwrite file fields with local state if managed manually
        // Note: form inputs for files might be empty/string, but we want the File[] state
        payload.attachtradeLicenseDoc = uploadLicense;
        payload.attachMembershipDoc = uploadMembership;
        payload.attachCompanyProfile = uploadCompanyProfile;
        payload.previousFromCountryData = previousCountryData;
        return payload;
    };

    React.useEffect(() => {
        return () => {
            const values = getManagedValues(getValues());
            dispatch(updateFormValues(values));
            dispatch(updateCommonValues(values));
        };
    }, [
        dispatch,
        getValues,
        uploadLicense,
        uploadMembership,
        uploadCompanyProfile,
        managedFields
    ]);

    const onSubmit = async (data: any) => {
        console.log('ContactDetail submit', data);
        setLoading(true);
        const values = getManagedValues(data);
        dispatch(updateFormValues(values));
        dispatch(updateCommonValues(values));
        if (haveYouFill !== 'yes') {
            const response = await otpService.gerateOTP(data?.businessEmail,scopeKeyLeadManagement);
            if (response.statusCode === 200) {
                toast.success(response.statusMessage, {
                    theme: 'light',
                    autoClose: 2000
                });
                setStep((prev) => prev + 1);
                scrollToSection();
            }
        } else {
            setStep((prev) => prev + 1);
            scrollToSection();
        }

        setLoading(false);
    };

    const filteredBasicDetails = basicDetailsFields.filter(
        (field) => field.name !== 'haveYouFill' && field.name !== 'member'
    );

    const fieldsToRender = isFormFilled
        ? filteredBasicDetails
        : basicDetailsFields;

    const showFields = isFormFilled || isChamberMember;

    const dropdownOptions = {
        haveYouFill: defaultValueForSelect,
        member: defaultValueForSelect
    };

    useEffect(() => {
        if (haveYouFill === 'yes') {
            dispatch(setIsFormFilled(true));
            dispatch(setIsChamberMember(null));
        } else if (haveYouFill === 'no') {
            dispatch(setIsFormFilled(false));
        } else {
            dispatch(setIsFormFilled(null));
        }
    }, [haveYouFill, dispatch]);

    useEffect(() => {
        if (member === 'yes') {
            dispatch(setIsChamberMember(true));
        } else if (member === 'no') {
            dispatch(setIsChamberMember(false));
        } else {
            dispatch(setIsChamberMember(null));
        }
    }, [member, dispatch]);

    const getFormDate = async (businessEmail: string) => {
        if (!businessEmail) return;
        try {
            const response =
                (await expansionInquiryService.getAlreadyFormFillData(
                    businessEmail,
                    Number(formValues?.dcMembershipNumber)
                )) as {
                    success?: boolean;
                    message?: string;
                    data?: {
                        totalCount?: number;
                        items: any[];
                    };
                };

            if (response.success && response.data?.items?.length == 1) {
                const data = response.data.items[0];
                console.log('data', data);
                dispatch(setFormId(Number(data?.id)));
                dispatch(setInquiryVersion(Number(data?.inquiryVersion + 1)));
                setValue('companyName', data?.companyName);
                setValue('dcMembershipNumber', data?.dcMembershipNumber);
                setValue('tradeLicenseNumber', data?.tradeLicenseNumber);
                setValue('contactName', data?.contactName);
                setValue('businessEmail', data?.businessEmail);
                const tradeLicenseDocument: any[] = [
                    {
                        objectEntryId: data?.attachTradeLicense?.id,
                        documentName: data?.attachTradeLicense?.name ?? '',
                        href: data?.attachTradeLicense?.link?.href ?? '',
                        name: data?.attachTradeLicense?.name ?? ''
                    }
                ];
                setValue('attachtradeLicenseDoc', tradeLicenseDocument);
                setUploadLicense(tradeLicenseDocument);
                const dcMembershipCertificateDocument: any[] = [
                    {
                        objectEntryId: data?.dcMembershipCertificate?.id,
                        documentName: data?.dcMembershipCertificate?.name ?? '',
                        href: data?.dcMembershipCertificate?.link?.href ?? '',
                        name: data?.dcMembershipCertificate?.name ?? ''
                    }
                ];
                setUploadMembership(dcMembershipCertificateDocument);
                setValue(
                    'attachMembershipDoc',
                    dcMembershipCertificateDocument
                );
                const previousCountryData =
                    data?.expansionInquiryToExpansionInquiryLead?.map(
                        (item: any) => {
                            const purposes = item?.businessPurpose || [];

                            const otherPurpose = purposes.find(
                                (p: any) => p.name === 'Others'
                            );
                            const normalPurposes = purposes
                                .filter((p: any) => p.name !== 'Others')
                                .map((p: any) => p.name);

                            let businessPurpose = normalPurposes.join(', ');

                            if (otherPurpose) {
                                const otherValue = item?.otherBusinessPurpose
                                    ? `Others: ${item.otherBusinessPurpose}`
                                    : 'Others';

                                businessPurpose = businessPurpose
                                    ? `${businessPurpose}, ${otherValue}`
                                    : otherValue;
                            }

                            return {
                                ...item,
                                targetMarket: item?.targetMarkets?.name,
                                previousExperience:
                                    item?.previousExperience === true
                                        ? 'Yes'
                                        : 'No',
                                purposeOfExpansion: businessPurpose,
                                marketPriority: item?.isPrimaryTargetMarket
                                    ? 'Primary'
                                    : 'Secondary'
                            };
                        }
                    );

                setPreviousCountryData(previousCountryData);
            }

            if (response.success && response.data?.totalCount == 0) {
                toast.error(
                    `You have not previously filled out the form using ${businessEmail}`,
                    {
                        theme: 'light',
                        autoClose: 2000
                    }
                );
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to submit inquiry', {
                theme: 'light',
                autoClose: 4000
            });
        }
    };

    useEffect(() => {
        if (haveYouFill === 'yes' && businessEmail) {
            getFormDate(businessEmail);
        }
    }, [haveYouFill, businessEmail]);
    return (
        <>
            {loading && <Loader />}
            <form onSubmit={handleSubmit(onSubmit)}>
                {showFields && (
                    <>
                        <ClayLayout.Row
                            justify="start"
                            className="basic_detail_form"
                        >
                            {fieldsToRender.map((field) => (
                                <ClayLayout.Col
                                    key={field.name}
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={6}
                                    xs={12}
                                >
                                    <FormComponent
                                        {...field}
                                        register={register}
                                        control={control}
                                        errors={formErrors}
                                        watch={watch}
                                        setError={setError}
                                        errorTitle={field.errorMessage}
                                        disabled={
                                            haveYouFill === 'yes' &&
                                            [
                                                'companyName',
                                                'dcMembershipNumber',
                                                'tradeLicenseNumber'
                                            ].includes(field.name)
                                                ? true
                                                : field.disabled
                                        }
                                        options={
                                            (dropdownOptions as any)[
                                                field.name
                                            ] || []
                                        }
                                        selectedFiles={
                                            fileHandling[
                                                field.name as keyof typeof fileHandling
                                            ]?.selectedFiles
                                        }
                                        setSelectedFiles={
                                            fileHandling[
                                                field.name as keyof typeof fileHandling
                                            ]?.setSelectedFiles
                                        }
                                    />
                                </ClayLayout.Col>
                            ))}
                        </ClayLayout.Row>

                        <h2 className="section-title">
                            Point of Contact Details:
                        </h2>
                        <ClayLayout.Row
                            justify="start"
                            className="basic_detail_form"
                        >
                            {contactDetailsFields.map((field) => (
                                <ClayLayout.Col
                                    key={field.name}
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={6}
                                    xs={12}
                                >
                                    <FormComponent
                                        {...field}
                                        register={register}
                                        control={control}
                                        errors={formErrors}
                                        watch={watch}
                                        setError={setError}
                                        errorTitle={field.errorMessage}
                                        disabled={
                                            (haveYouFill === 'yes' &&
                                                [
                                                    'contactName',
                                                    'businessEmail'
                                                ].includes(field.name)) ||
                                            field.disabled
                                        }
                                        selectedFiles={
                                            fileHandling[
                                                field.name as keyof typeof fileHandling
                                            ]?.selectedFiles
                                        }
                                        setSelectedFiles={
                                            fileHandling[
                                                field.name as keyof typeof fileHandling
                                            ]?.setSelectedFiles
                                        }
                                        options={[]}
                                    />
                                </ClayLayout.Col>
                            ))}
                        </ClayLayout.Row>

                        <h2 className="section-title">
                            Senior Management Details:
                        </h2>
                        <ClayLayout.Row
                            justify="start"
                            className="basic_detail_form"
                        >
                            {seniorManagementDetailsFields?.map((field) => {
                                const colSize =
                                    field?.name == 'companyPhysicalAddress'
                                        ? 12
                                        : 6;
                                return (
                                    <ClayLayout.Col
                                        key={field.name}
                                        xl={colSize}
                                        lg={colSize}
                                        md={colSize}
                                        sm={colSize}
                                        xs={12}
                                    >
                                        <FormComponent
                                            placeholder={field.placeholder}
                                            label={field.label}
                                            name={field.name}
                                            register={register}
                                            type={field.type}
                                            control={control}
                                            errors={formErrors}
                                            watch={watch}
                                            setError={setError}
                                            errorTitle={field.errorMessage}
                                            required={field?.required}
                                            selectedFiles={
                                                fileHandling[
                                                    field.name as keyof typeof fileHandling
                                                ]?.selectedFiles
                                            }
                                            setSelectedFiles={
                                                fileHandling[
                                                    field.name as keyof typeof fileHandling
                                                ]?.setSelectedFiles
                                            }
                                            component={field.component}
                                        />
                                    </ClayLayout.Col>
                                );
                            })}
                        </ClayLayout.Row>
                    </>
                )}

                <div className="basic-details-button-section">
                    <Button
                        type="submit"
                        className="bottom-cut"
                        style={primaryButtonStyle}
                    >
                        <span className="bottom-cut-span"></span>
                        Continue
                    </Button>
                </div>
            </form>
        </>
    );
};

export default ContactDetail;
